import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { AddOptionsInput } from 'module/ui/components/atoms/choices/add-option';
import { SortableOptions } from 'module/ui/components/atoms/choices/list';
import { Form } from 'module/ui/components/atoms/form';
import { Button, InputField, Select, Checkbox } from '@rocketlane/ui';
import { AnswerTypeOption } from 'api/custom-section/types';
import { PageType } from 'api/page-template/types';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ChoicesContainer,
  CheckboxContainer,
} from 'components/organisms/document-editor/extensions/custom-section/ui/sections-type/extract-info/styles';
import { SharedConfigurationBar } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-bar';
import { SectionHeading } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-heading';
import {
  SectionCard,
  TextAreaContainer,
  SectionContent,
  SectionDivider,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { ConfigurationBar } from '../../../shared/section-styles';
import type { Option } from '@rocketlane/ui';

export const PromptInput = ({
  prompt,
  setPrompt,
  onGenerate,
  onCancel,
  onUndo,
  allowResolve,
  pageType,
}) => {
  const [form] = Form.useForm<{
    title: string;
    question: string;
    aiContext: string;
  }>();
  const [showNext, setShowNext] = useState(!prompt.title?.trim());
  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.extract_info_section.title_placeholder',
  );

  const contextPlaceholder = useTranslation(
    'rich_text_editor.sections.section.context_placeholder',
  );
  const singleChoiceLabel = useTranslation(
    'rich_text_editor_sections_single_choice' as any,
  );
  const multiChoiceLabel = useTranslation(
    'rich_text_editor_sections_multiple_choice' as any,
  );
  const aiGeneratedChoicesLabel = useTranslation(
    'rich_text_editor.sections.extract_info_section.ai_generated_choices',
  );

  const aiContextRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [choices, setChoices] = useState(prompt.choices.choices);

  useEffect(() => {
    form.setFieldsValue({
      title: prompt.title,
      question: prompt.question,
      aiContext: prompt.aiContext,
    });
  }, [
    form,
    prompt.aiContext,
    prompt.aiContextChecked,
    prompt.question,
    prompt.title,
  ]);

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.select();
    });
  }, []);

  const options = useMemo(() => {
    return [
      { label: singleChoiceLabel, value: AnswerTypeOption.SingleChoice },
      { label: multiChoiceLabel, value: AnswerTypeOption.MultiChoice },
    ];
  }, [singleChoiceLabel, multiChoiceLabel]);
  const selectedOption = useMemo(() => {
    return options.find(
      (option) => option.value === prompt.choices?.answerType,
    );
  }, [options, prompt.choices?.answerType]);

  const addOption = useCallback(
    (option) => {
      const newOptions = choices.concat({
        ...option,
        value:
          choices.reduce((max, option) => Math.max(max, option.value), 0) + 1,
        position: choices.length + 1,
      });

      setChoices(newOptions);
    },
    [choices, setChoices],
  );
  const removeOption = useCallback(
    (option) => {
      const newOptions = choices.filter((x) => x.value !== option.value);

      setChoices(
        newOptions.map((x, index) => ({
          ...x,
          position: index + 1,
        })),
      );
    },
    [choices, setChoices],
  );
  const updateOptions = (options) => {
    const existingChoice = choices.find(
      (existing) => existing.value === options.value,
    );

    if (existingChoice !== -1) {
      setChoices((previous) => {
        return previous.map((choice) => {
          if (choice?.value === existingChoice?.value) {
            return options;
          }

          return choice;
        });
      });
    }
  };

  const triggerGenerate = () => {
    const promptHash = {
      ...prompt,
      title: form.getFieldValue('title'),
      question: form.getFieldValue('question'),
      aiContextChecked: form.getFieldValue('aiContextChecked'),
      aiContext: form.getFieldValue('aiContext'),
      choices: { ...prompt.choices, choices },
    };

    setPrompt(promptHash);
    onGenerate(promptHash);
  };

  const [errors, setErrors] = useState<boolean>(false);

  const validateForm = (formData: any) => {
    const errors = formData.getFieldsError();

    let showError = false;

    errors.forEach((error: any) => {
      if (error.errors.length > 0 && !showError) {
        showError = true;
      }
    });
    setErrors(showError);
  };

  const aiGeneratedChoicesPlaceholder = useTranslation(
    'rich_text_editor.sections.extract_info_section.ai_generated_choices_description',
  );

  const canShowPreviewButton =
    pageType === PageType.Meeting || pageType === PageType.Email;

  const isDisabled = errors;

  return (
    <SectionCard>
      <SectionHeading
        title="editor.sections.extract_info_section.title"
        onClose={onUndo}
        showCloseButton={!isDisabled}
      />
      <SectionContent>
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label={
              <FormattedMessage id="rich_text_editor.sections.question_section.title" />
            }
          >
            <InputField
              type="text"
              ref={titleRef}
              onChange={(e) => setPrompt({ ...prompt, title: e.target.value })}
              placeholder={titlePlaceholder}
              autoFocus
            />
          </Form.Item>
          {!showNext && (
            <>
              <Form.Item
                name="aiContext"
                label={
                  <FormattedMessage id="rich_text_editor.sections.question_section.add_ai_context" />
                }
              >
                <TextAreaContainer
                  id="aiContext"
                  ref={aiContextRef}
                  placeholder={contextPlaceholder}
                />
              </Form.Item>

              <Form.Item
                label={
                  <FormattedMessage id="rich_text_editor_sections_extract_info_section.answer_type" />
                }
              >
                <Select
                  options={options as unknown as Option[]}
                  value={selectedOption as unknown as Option}
                  onChange={(value) =>
                    setPrompt({
                      ...prompt,
                      choices: {
                        ...prompt.choices,
                        answerType: (value as unknown as Option).value,
                      },
                    })
                  }
                />
              </Form.Item>

              <Form.Item
                label={
                  <FormattedMessage id="rich_text_editor_sections_extract_info_section.choices" />
                }
              >
                <ChoicesContainer>
                  <SortableOptions
                    shouldDrag={false}
                    validateForm={validateForm}
                    options={choices}
                    removeOption={removeOption}
                    updateOptions={updateOptions}
                    editInputOption={updateOptions}
                  />
                  <AddOptionsInput
                    shouldValidate={false}
                    existingOptions={choices}
                    addOption={addOption}
                    addedAsFormItem={false}
                  />
                </ChoicesContainer>
              </Form.Item>
              <Form.Item name="aiContextChecked">
                <CheckboxContainer>
                  <Checkbox
                    color="black"
                    label={aiGeneratedChoicesLabel}
                    checked={prompt.choices.generateNewChoices}
                    onCheckedChange={(checked) => {
                      setPrompt({
                        ...prompt,
                        choices: {
                          ...prompt.choices,
                          generateNewChoices: checked,
                        },
                      });
                    }}
                    helperText={aiGeneratedChoicesPlaceholder}
                  />
                </CheckboxContainer>
              </Form.Item>
            </>
          )}
        </Form>
        {showNext ? (
          <ConfigurationBar>
            <Button
              variant="primary"
              disabled={!prompt.title?.trim()}
              onClick={() => {
                setShowNext(false);
              }}
            >
              <FormattedMessage id="next" />
            </Button>
          </ConfigurationBar>
        ) : (
          <>
            <SectionDivider />
            <SharedConfigurationBar
              prompt={prompt}
              setPrompt={setPrompt}
              onCancel={onCancel}
              onSubmit={triggerGenerate}
              validation={{
                isDisabled,
              }}
              allowResolve={allowResolve}
              actionBarConfig={{
                canShowPreviewButton,
                canWrapTrashButton: true,
              }}
              pageType={pageType}
            />
          </>
        )}
      </SectionContent>
    </SectionCard>
  );
};
